export enum VaultCodeEnum {
  Book = "a",
  Budget = "bg",
  Category = "ca",
  Classification = "cl",
  Institution = "in",
  Key = "k",
  ReferenceData = "rd",
  Estimate = "es",
  Symbol = "s",
  Transaction = "t",
  SourceTransaction = "u",
  /** Source balance snapshots (e.g. Fiskil GET balances per account). */
  SourceBalance = "sbl",
  SourceBook = "bs",
  Preference = "p",
  EstimateAction = "ea",
  Scenario = "sc",
  ScenarioGroup = "sg",
  SourceCategory = "soc",
  Connector = "con",
  VaultFile = "vf",
  Wizard = "wz",
  SyncStatus = "ss",
  Plugin = "pl",
  SavingGoal = "svg",
  UNKNOWN = "unknown",
}

/**
 * Extracts the vault code prefix from a cipher name (e.g. "t-123" -> Transaction) and validates
 * it against the enum. Any prefix that is not a real enum member returns UNKNOWN rather than
 * being cast blindly, since an ordinary Bitwarden cipher can have an unrecognised name prefix.
 */
export function getVaultObjectCode(cipherName: string): VaultCodeEnum {
  const prefix = cipherName.split("-")[0];
  return Object.values(VaultCodeEnum).includes(prefix as VaultCodeEnum)
    ? (prefix as VaultCodeEnum)
    : VaultCodeEnum.UNKNOWN;
}

export type StoreModelNames =
  | "AccountStoreModel"
  | "CategoryStoreModel"
  | "ClassificationStoreModel"
  | "InstitutionStoreModel"
  | "KeyStoreModel"
  | "ReferenceDataStoreModel"
  | "EstimateStoreModel"
  | "SymbolStoreModel"
  | "TransactionStoreModel"
  | "SourceTransactionStoreModel"
  | "SourceBalanceStoreModel"
  | "SourceBookStoreModel"
  | "PreferenceStoreModel"
  | "EstimateActionStoreModel"
  | "ScenarioStoreModel"
  | "ScenarioGroupStoreModel"
  | "BudgetStoreModel"
  | "SavingGoalStoreModel"
  | "SourceCategoryStoreModel"
  | "ConnectorStoreModel"
  | "VaultFileStoreModel"
  | "WizardStoreModel"
  | "SyncStatusStoreModel"
  | "PluginStoreModel"
  | "StoreModel";

export const StoreModelNameByVaultCode: Record<VaultCodeEnum, StoreModelNames> = {
  [VaultCodeEnum.Book]: "AccountStoreModel",
  [VaultCodeEnum.Category]: "CategoryStoreModel",
  [VaultCodeEnum.Classification]: "ClassificationStoreModel",
  [VaultCodeEnum.Institution]: "InstitutionStoreModel",
  [VaultCodeEnum.Key]: "KeyStoreModel",
  [VaultCodeEnum.ReferenceData]: "ReferenceDataStoreModel",
  [VaultCodeEnum.Estimate]: "EstimateStoreModel",
  [VaultCodeEnum.Symbol]: "SymbolStoreModel",
  [VaultCodeEnum.Transaction]: "TransactionStoreModel",
  [VaultCodeEnum.SourceTransaction]: "SourceTransactionStoreModel",
  [VaultCodeEnum.SourceBalance]: "SourceBalanceStoreModel",
  [VaultCodeEnum.SourceBook]: "SourceBookStoreModel",
  [VaultCodeEnum.Preference]: "PreferenceStoreModel",
  [VaultCodeEnum.EstimateAction]: "EstimateActionStoreModel",
  [VaultCodeEnum.Scenario]: "ScenarioStoreModel",
  [VaultCodeEnum.ScenarioGroup]: "ScenarioGroupStoreModel",
  [VaultCodeEnum.Budget]: "BudgetStoreModel",
  [VaultCodeEnum.SavingGoal]: "SavingGoalStoreModel",
  [VaultCodeEnum.SourceCategory]: "SourceCategoryStoreModel",
  [VaultCodeEnum.Connector]: "ConnectorStoreModel",
  [VaultCodeEnum.VaultFile]: "VaultFileStoreModel",
  [VaultCodeEnum.Wizard]: "WizardStoreModel",
  [VaultCodeEnum.SyncStatus]: "SyncStatusStoreModel",
  [VaultCodeEnum.Plugin]: "PluginStoreModel",
  [VaultCodeEnum.UNKNOWN]: "StoreModel",
};

/** Use AI to revert the key from StoreModelNameByVaultCode or show me how to do tghat in typescript **/
export const StoreVaultCodeByModelName: Record<StoreModelNames, VaultCodeEnum> = {
  AccountStoreModel: VaultCodeEnum.Book,
  CategoryStoreModel: VaultCodeEnum.Category,
  ClassificationStoreModel: VaultCodeEnum.Classification,
  InstitutionStoreModel: VaultCodeEnum.Institution,
  KeyStoreModel: VaultCodeEnum.Key,
  ReferenceDataStoreModel: VaultCodeEnum.ReferenceData,
  EstimateStoreModel: VaultCodeEnum.Estimate,
  SymbolStoreModel: VaultCodeEnum.Symbol,
  TransactionStoreModel: VaultCodeEnum.Transaction,
  SourceTransactionStoreModel: VaultCodeEnum.SourceTransaction,
  SourceBalanceStoreModel: VaultCodeEnum.SourceBalance,
  SourceBookStoreModel: VaultCodeEnum.SourceBook,
  PreferenceStoreModel: VaultCodeEnum.Preference,
  EstimateActionStoreModel: VaultCodeEnum.EstimateAction,
  ScenarioStoreModel: VaultCodeEnum.Scenario,
  ScenarioGroupStoreModel: VaultCodeEnum.ScenarioGroup,
  BudgetStoreModel: VaultCodeEnum.Budget,
  SavingGoalStoreModel: VaultCodeEnum.SavingGoal,
  SourceCategoryStoreModel: VaultCodeEnum.SourceCategory,
  ConnectorStoreModel: VaultCodeEnum.Connector,
  VaultFileStoreModel: VaultCodeEnum.VaultFile,
  WizardStoreModel: VaultCodeEnum.Wizard,
  PluginStoreModel: VaultCodeEnum.Plugin,
  StoreModel: VaultCodeEnum.UNKNOWN,
  SyncStatusStoreModel: VaultCodeEnum.SyncStatus,
};
